import { Routes } from '@angular/router';
import {
  redirectToActivePartner,
  requireAnyCapability,
  requireCapabilities,
  requirePartnerAccess,
  setActivePartnerFromRoute,
} from '@sisoma/auth';

import { Shell } from './core/layout/shell';
import { unsavedChangesGuard } from './features/compliance/policy-packs/unsaved-changes.guard';

/**
 * All feature pages are lazy-loaded children of the shell, so the sidenav and
 * main-area frame persist across navigation. Each route's `title` feeds the
 * document title via `PartnerTitleStrategy`; `pageTitle` data is bound to
 * component inputs by `withComponentInputBinding`.
 *
 * Adding a section = add a child route here + an entry in
 * `core/navigation/nav-items.ts`.
 */
export const routes: Routes = [
  // The 403 page must sit OUTSIDE the partner-access-guarded shell and be
  // matched BEFORE it — otherwise the guard's redirect here would re-trigger
  // the guard (or fall through the shell's `**`) and loop. Rendered standalone,
  // without the sidenav frame.
  {
    path: 'forbidden',
    title: 'Not authorised',
    loadComponent: () =>
      import('./features/not-authorised/not-authorised').then((m) => m.NotAuthorised),
  },
  {
    path: '',
    component: Shell,
    // Coarse gate for the whole surface (fp1-04): logged-out → shared login;
    // logged-in without partner access → `/forbidden`. Per-feature
    // `requireCapabilities(...)` guards attach to individual child routes as
    // real features land (fp1-07+).
    canActivate: [requirePartnerAccess],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      {
        path: 'dashboard',
        title: 'Dashboard',
        loadComponent: () => import('./features/dashboard/dashboard').then((m) => m.Dashboard),
      },
      {
        path: 'example',
        title: 'Example',
        data: { pageTitle: 'Example' },
        loadComponent: () =>
          import('./features/placeholder/placeholder-page').then((m) => m.PlaceholderPage),
      },
      {
        path: 'example-group/child',
        title: 'Example Child',
        data: { pageTitle: 'Example Child' },
        loadComponent: () =>
          import('./features/placeholder/placeholder-page').then((m) => m.PlaceholderPage),
      },
      {
        path: 'example-group/another',
        title: 'Another Child',
        data: { pageTitle: 'Another Child' },
        loadComponent: () =>
          import('./features/placeholder/placeholder-page').then((m) => m.PlaceholderPage),
      },
      // Settings follows the same dual-path shape as the other partner-scoped
      // features: the bare path canonicalises to the active partner's id URL,
      // which is where the capability gate lives. It is an index of its
      // sub-pages — each of those carries its own capability, so this page
      // shows only what the viewer can actually reach.
      {
        path: 'settings',
        title: 'Settings',
        canActivate: [redirectToActivePartner('settings')],
        loadComponent: () => import('./features/settings/settings').then((m) => m.Settings),
      },
      {
        path: 'partner/:id/settings',
        title: 'Settings',
        canActivate: [setActivePartnerFromRoute, requireCapabilities('partner:manage_settings')],
        loadComponent: () => import('./features/settings/settings').then((m) => m.Settings),
      },
      // Single sign-on is a settings sub-page gated on `partner:manage_sso`
      // rather than the parent's `partner:manage_settings`. That is the whole
      // point of the capability: an Owner can delegate the Entra connection to
      // an IT person without also handing over billing and roles, so someone
      // holding only this one must be able to reach the page.
      {
        path: 'settings/sso',
        title: 'Single sign-on',
        canActivate: [redirectToActivePartner('settings', 'sso')],
        loadComponent: () => import('./features/settings/sso/sso').then((m) => m.SsoSettings),
      },
      {
        path: 'partner/:id/settings/sso',
        title: 'Single sign-on',
        canActivate: [setActivePartnerFromRoute, requireCapabilities('partner:manage_sso')],
        loadComponent: () => import('./features/settings/sso/sso').then((m) => m.SsoSettings),
      },
      // Audit Log is a settings sub-page (PA-AUDIT §6), gated on its own
      // capability rather than the parent's. Same static-alias +
      // `partner/:id/...` pair as the features below.
      {
        path: 'settings/audit',
        title: 'Audit Log',
        canActivate: [redirectToActivePartner('settings', 'audit')],
        loadComponent: () =>
          import('./features/settings/audit-log/audit-log').then((m) => m.AuditLog),
      },
      {
        path: 'partner/:id/settings/audit',
        title: 'Audit Log',
        canActivate: [setActivePartnerFromRoute, requireCapabilities('partner:view_audit')],
        loadComponent: () =>
          import('./features/settings/audit-log/audit-log').then((m) => m.AuditLog),
      },
      // Members, Roles, Invites and Workspaces live at `partner/:id/...`; the
      // sidebar links to the static `members`/`roles`/`invites`/`workspaces`
      // paths, which `redirectToActivePartner(...)` canonicalises to the active
      // partner's id URL (no partner switcher yet).
      {
        path: 'members',
        title: 'Members',
        canActivate: [redirectToActivePartner('members')],
        loadComponent: () => import('./features/members/members').then((m) => m.Members),
      },
      {
        path: 'partner/:id/members',
        title: 'Members',
        canActivate: [setActivePartnerFromRoute, requireCapabilities('partner:manage_members')],
        loadComponent: () => import('./features/members/members').then((m) => m.Members),
      },
      {
        path: 'roles',
        title: 'Roles',
        canActivate: [redirectToActivePartner('roles')],
        loadComponent: () => import('./features/roles/roles').then((m) => m.Roles),
      },
      {
        path: 'partner/:id/roles',
        title: 'Roles',
        canActivate: [setActivePartnerFromRoute, requireCapabilities('partner:manage_roles')],
        loadComponent: () => import('./features/roles/roles').then((m) => m.Roles),
      },
      {
        path: 'invites',
        title: 'Invites',
        canActivate: [redirectToActivePartner('invites')],
        loadComponent: () => import('./features/invites/invites').then((m) => m.Invites),
      },
      {
        path: 'partner/:id/invites',
        title: 'Invites',
        canActivate: [setActivePartnerFromRoute, requireCapabilities('partner:invite')],
        loadComponent: () => import('./features/invites/invites').then((m) => m.Invites),
      },
      {
        path: 'workspaces',
        title: 'Workspaces',
        canActivate: [redirectToActivePartner('workspaces')],
        loadComponent: () => import('./features/workspaces/workspaces').then((m) => m.Workspaces),
      },
      {
        path: 'partner/:id/workspaces',
        title: 'Workspaces',
        canActivate: [setActivePartnerFromRoute, requireCapabilities('partner:manage_workspaces')],
        loadComponent: () => import('./features/workspaces/workspaces').then((m) => m.Workspaces),
      },
      // Content & Categories (PA-CONTENT). Same route-pair convention as the
      // features above; `redirectToActivePartner` takes the sub-path segment by
      // segment, so the alias `/content/categories` canonicalises to
      // `/partner/:id/content/categories`. The list screen sits at
      // `content/library` rather than `content/content` — the group is already
      // called Content, and `content/content` reads as a typo in the URL bar.
      {
        path: 'content/categories',
        title: 'Categories',
        canActivate: [redirectToActivePartner('content', 'categories')],
        loadComponent: () =>
          import('./features/content/categories/categories').then((m) => m.Categories),
      },
      {
        path: 'partner/:id/content/categories',
        title: 'Categories',
        canActivate: [setActivePartnerFromRoute, requireCapabilities('content:manage_categories')],
        loadComponent: () =>
          import('./features/content/categories/categories').then((m) => m.Categories),
      },
      {
        path: 'content/library',
        title: 'Content',
        canActivate: [redirectToActivePartner('content', 'library')],
        loadComponent: () =>
          import('./features/content/library/library').then((m) => m.ContentLibrary),
      },
      {
        path: 'partner/:id/content/library',
        title: 'Content',
        canActivate: [setActivePartnerFromRoute, requireCapabilities('content:view')],
        loadComponent: () =>
          import('./features/content/library/library').then((m) => m.ContentLibrary),
      },
      // Compliance (PA-COMPL). Same static-alias + `partner/:id/...` pair as
      // every gated feature above: the sidebar links to `/compliance...`, the
      // alias canonicalises to the active partner's id URL, and the twin is
      // where the capability is actually enforced.
      //
      // ⚠️ Order matters. `redirectToActivePartner` builds the twin URL from the
      // segments it is given, so `compliance/bot/triage` must be registered
      // before `compliance/bot` would swallow it — Angular matches these in
      // declaration order and `compliance/bot` is not a prefix match, but the
      // longest path is listed first anyway so a future `**`-style change cannot
      // silently reorder them.
      // Approvals (PA-COMPL wave D). Longest first within its own pair, for the
      // same reason as the bot routes below.
      //
      // ⚠️ The detail alias is the first route in this app whose sub-path
      // carries a parameter. `redirectToActivePartner` substitutes a `':name'`
      // segment from the aliased route's own params, so
      // `/compliance/approvals/areq-1` canonicalises to
      // `/partner/:id/compliance/approvals/areq-1` rather than dropping the
      // request id and landing on a detail page for nothing.
      //
      // Both twins gate on `compliance:approve`, not `compliance:view`: this is
      // a worklist of decisions, and everything on it is unusable without the
      // authority to decide. It is also the client-side gate the legacy creator
      // screen never had.
      {
        path: 'compliance/approvals/:requestId',
        title: 'Approval request',
        canActivate: [redirectToActivePartner('compliance', 'approvals', ':requestId')],
        loadComponent: () =>
          import('./features/compliance/Approvals/approval-detail/approval-detail').then(
            (m) => m.ComplianceApprovalDetail,
          ),
      },
      {
        path: 'partner/:id/compliance/approvals/:requestId',
        title: 'Approval request',
        canActivate: [setActivePartnerFromRoute, requireCapabilities('compliance:approve')],
        loadComponent: () =>
          import('./features/compliance/Approvals/approval-detail/approval-detail').then(
            (m) => m.ComplianceApprovalDetail,
          ),
      },
      {
        path: 'compliance/approvals',
        title: 'Approvals',
        canActivate: [redirectToActivePartner('compliance', 'approvals')],
        loadComponent: () =>
          import('./features/compliance/Approvals/approval-home/approvals').then(
            (m) => m.ComplianceApprovals,
          ),
      },
      {
        path: 'partner/:id/compliance/approvals',
        title: 'Approvals',
        canActivate: [setActivePartnerFromRoute, requireCapabilities('compliance:approve')],
        loadComponent: () =>
          import('./features/compliance/Approvals/approval-home/approvals').then(
            (m) => m.ComplianceApprovals,
          ),
      },
      {
        path: 'compliance/bot/triage',
        title: 'Triage Rules',
        canActivate: [redirectToActivePartner('compliance', 'bot', 'triage')],
        loadComponent: () =>
          import('./features/compliance/triage-rules/triage-rules-home/triage-rules').then(
            (m) => m.ComplianceTriageRules,
          ),
      },
      {
        path: 'partner/:id/compliance/bot/triage',
        title: 'Triage Rules',
        // GOV-03 (O3/U28): every triage route is `manage_policies`, reads
        // included, so a `view` holder would only reach a screen that 403s.
        canActivate: [setActivePartnerFromRoute, requireCapabilities('compliance:manage_policies')],
        loadComponent: () =>
          import('./features/compliance/triage-rules/triage-rules-home/triage-rules').then(
            (m) => m.ComplianceTriageRules,
          ),
      },
      {
        path: 'compliance/bot',
        title: 'Compliance Bot',
        canActivate: [redirectToActivePartner('compliance', 'bot')],
        loadComponent: () =>
          import('./features/compliance/Overview/bot-overview').then(
            (m) => m.ComplianceBotOverview,
          ),
      },
      {
        path: 'partner/:id/compliance/bot',
        title: 'Compliance Bot',
        canActivate: [setActivePartnerFromRoute, requireCapabilities('compliance:view')],
        loadComponent: () =>
          import('./features/compliance/Overview/bot-overview').then(
            (m) => m.ComplianceBotOverview,
          ),
      },
      // Policy packs (GOV-04): the list and one pack. Every pack route in the
      // API needs `manage_policies`, reads included (the controller puts it on
      // each one), so both twins gate on it, like Triage Rules; a `view` holder
      // would only reach screens that 403. Registered after the bot routes and
      // before Rule changes, matching the sidebar. Longest path first, and the
      // detail alias carries `:packId` across.
      {
        path: 'compliance/policy-packs/:packId',
        title: 'Policy pack',
        canActivate: [redirectToActivePartner('compliance', 'policy-packs', ':packId')],
        loadComponent: () =>
          import('./features/compliance/policy-packs/pack-detail/pack-detail').then(
            (m) => m.CompliancePolicyPackDetail,
          ),
      },
      {
        path: 'partner/:id/compliance/policy-packs/:packId',
        title: 'Policy pack',
        canActivate: [setActivePartnerFromRoute, requireCapabilities('compliance:manage_policies')],
        // A draft's unsaved edits are asked about before leaving (ruling Q8.4).
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () =>
          import('./features/compliance/policy-packs/pack-detail/pack-detail').then(
            (m) => m.CompliancePolicyPackDetail,
          ),
      },
      {
        path: 'compliance/policy-packs',
        title: 'Policy packs',
        canActivate: [redirectToActivePartner('compliance', 'policy-packs')],
        loadComponent: () =>
          import('./features/compliance/policy-packs/pack-list/pack-list').then(
            (m) => m.CompliancePolicyPacks,
          ),
      },
      {
        path: 'partner/:id/compliance/policy-packs',
        title: 'Policy packs',
        canActivate: [setActivePartnerFromRoute, requireCapabilities('compliance:manage_policies')],
        loadComponent: () =>
          import('./features/compliance/policy-packs/pack-list/pack-list').then(
            (m) => m.CompliancePolicyPacks,
          ),
      },
      // Rule changes (GOV-07): the approval queue for changes to the compliance
      // rules themselves. Registered after the bot routes it governs, matching
      // the sidebar, which lists it directly after Triage Rules.
      //
      // The twin gates on `manage_policies` OR `manage_sections` — the
      // registry's any-of entry carries the same pair (ruling T6).
      // `manage_policies` governs triage and policy packs, `manage_sections`
      // governs sections, and either is enough to have decisions here. An
      // all-of would lock out every firm at launch, when sections are hidden
      // (D19) and nobody holds `manage_sections`.
      //
      // The detail pair comes first: longest path first, like Approvals. Its
      // alias carries both params across, so `/compliance/governance/packs/pcr-1`
      // lands on `/partner/:id/compliance/governance/packs/pcr-1`. The twin
      // gates on the same any-of pair as the queue. Which of the two the
      // `:dimension` needs is the screen's job: it shows a locked panel, and
      // makes no read, for a dimension the viewer does not govern.
      {
        path: 'compliance/governance/:dimension/:requestId',
        title: 'Rule change',
        canActivate: [
          redirectToActivePartner('compliance', 'governance', ':dimension', ':requestId'),
        ],
        loadComponent: () =>
          import('./features/compliance/governance/governance-detail/governance-detail').then(
            (m) => m.GovernanceDetail,
          ),
      },
      {
        path: 'partner/:id/compliance/governance/:dimension/:requestId',
        title: 'Rule change',
        canActivate: [
          setActivePartnerFromRoute,
          requireAnyCapability('compliance:manage_policies', 'compliance:manage_sections'),
        ],
        loadComponent: () =>
          import('./features/compliance/governance/governance-detail/governance-detail').then(
            (m) => m.GovernanceDetail,
          ),
      },
      {
        path: 'compliance/governance',
        title: 'Rule changes',
        canActivate: [redirectToActivePartner('compliance', 'governance')],
        loadComponent: () =>
          import('./features/compliance/governance/governance-queue/governance-queue').then(
            (m) => m.GovernanceQueue,
          ),
      },
      {
        path: 'partner/:id/compliance/governance',
        title: 'Rule changes',
        canActivate: [
          setActivePartnerFromRoute,
          requireAnyCapability('compliance:manage_policies', 'compliance:manage_sections'),
        ],
        loadComponent: () =>
          import('./features/compliance/governance/governance-queue/governance-queue').then(
            (m) => m.GovernanceQueue,
          ),
      },
      // Test (GOV-09): try a post against the live rules or a draft, and the
      // runs recorded. Gated exactly like Rule changes above, on either
      // governing capability (never `compliance:view`, O14): a run varies the
      // rule set and pack (`manage_policies`) or the sections
      // (`manage_sections`), and the screen locks what the viewer does not
      // govern to Live.
      //
      // Longest path first. The aliases keep the query string, so "Test again"
      // (`/compliance/test?from=run-1`) lands with its `from` intact.
      {
        path: 'compliance/test/runs/:runId',
        title: 'Test run',
        canActivate: [redirectToActivePartner('compliance', 'test', 'runs', ':runId')],
        loadComponent: () =>
          import('./features/compliance/test-suite/run-detail/run-detail').then(
            (m) => m.TestRunDetail,
          ),
      },
      {
        path: 'partner/:id/compliance/test/runs/:runId',
        title: 'Test run',
        canActivate: [
          setActivePartnerFromRoute,
          requireAnyCapability('compliance:manage_policies', 'compliance:manage_sections'),
        ],
        loadComponent: () =>
          import('./features/compliance/test-suite/run-detail/run-detail').then(
            (m) => m.TestRunDetail,
          ),
      },
      {
        path: 'compliance/test/runs',
        title: 'Past test runs',
        canActivate: [redirectToActivePartner('compliance', 'test', 'runs')],
        loadComponent: () =>
          import('./features/compliance/test-suite/runs-list/runs-list').then(
            (m) => m.TestRunsList,
          ),
      },
      {
        path: 'partner/:id/compliance/test/runs',
        title: 'Past test runs',
        canActivate: [
          setActivePartnerFromRoute,
          requireAnyCapability('compliance:manage_policies', 'compliance:manage_sections'),
        ],
        loadComponent: () =>
          import('./features/compliance/test-suite/runs-list/runs-list').then(
            (m) => m.TestRunsList,
          ),
      },
      {
        path: 'compliance/test',
        title: 'Test',
        canActivate: [redirectToActivePartner('compliance', 'test')],
        loadComponent: () =>
          import('./features/compliance/test-suite/test-screen/test-screen').then(
            (m) => m.TestScreen,
          ),
      },
      {
        path: 'partner/:id/compliance/test',
        title: 'Test',
        canActivate: [
          setActivePartnerFromRoute,
          requireAnyCapability('compliance:manage_policies', 'compliance:manage_sections'),
        ],
        loadComponent: () =>
          import('./features/compliance/test-suite/test-screen/test-screen').then(
            (m) => m.TestScreen,
          ),
      },
      // Reports (wave F) gates on `compliance:reports` — the reports seat, which
      // implies `compliance:view` but is not implied by it (plan §6.2). The
      // Editor bundle holds `view` and not `reports`, so an Editor who types this
      // URL is turned away by the guard exactly as they are by the missing nav
      // entry.
      //
      // ⚠️ Reports is the first section in this app with CHILD routes: the shell
      // draws the report list and the shared filter bar, and each report renders
      // into its outlet. Two consequences worth knowing before editing:
      //
      // 1. `:id` does NOT reach the children. Angular's input binding does not
      //    inherit a parent's params into a child with its own non-empty path,
      //    so a report page reads the partner from `CapabilitiesService`, as
      //    every data service in this app already does. Only the shell binds it.
      // 2. `detail/:reportId` sits under its own literal segment rather than
      //    beside `:reportKey`. The two are the SAME shape — order cannot
      //    separate them, only a distinct segment can. That is the same trap
      //    plan §6 records on the API for `/reports/:viewId` versus
      //    `/reports/:reportId`, and the same answer.
      //
      // The static aliases mirror the child paths one for one, because
      // `redirectToActivePartner` canonicalises a whole sub-path and a reader
      // who bookmarks `/compliance/reports/posture` must land on their partner's
      // copy rather than on the section root.
      {
        path: 'compliance/reports',
        title: 'Compliance Reports',
        canActivate: [redirectToActivePartner('compliance', 'reports')],
        loadComponent: () =>
          import('./features/compliance/Reports/reports').then((m) => m.ComplianceReports),
      },
      {
        path: 'partner/:id/compliance/reports',
        title: 'Compliance Reports',
        canActivate: [setActivePartnerFromRoute, requireCapabilities('compliance:reports')],
        loadComponent: () =>
          import('./features/compliance/Reports/reports').then((m) => m.ComplianceReports),
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'posture' },
          {
            path: 'posture',
            title: 'Compliance posture',
            loadComponent: () =>
              import('./features/compliance/Reports/pages/posture').then((m) => m.ReportPosture),
          },
          {
            path: 'flagged-issues',
            title: 'Top flagged issues',
            loadComponent: () =>
              import('./features/compliance/Reports/pages/flagged-issues').then(
                (m) => m.ReportFlaggedIssues,
              ),
          },
          {
            path: 'coaching',
            title: 'Repeat-offender coaching',
            loadComponent: () =>
              import('./features/compliance/Reports/pages/coaching').then((m) => m.ReportCoaching),
          },
          {
            path: 'workspaces',
            title: 'Workspace league',
            loadComponent: () =>
              import('./features/compliance/Reports/pages/workspaces').then(
                (m) => m.ReportWorkspaces,
              ),
          },
          {
            path: 'approvals',
            title: 'Approvals operations',
            loadComponent: () =>
              import('./features/compliance/Reports/pages/approvals-ops').then(
                (m) => m.ReportApprovalsOps,
              ),
          },
          {
            path: 'coverage',
            title: 'Coverage and blind spots',
            loadComponent: () =>
              import('./features/compliance/Reports/pages/coverage').then((m) => m.ReportCoverage),
          },
          {
            path: 'skip-review',
            title: 'Skip-review queue',
            loadComponent: () =>
              import('./features/compliance/Reports/pages/skip-review').then(
                (m) => m.ReportSkipReview,
              ),
          },
          {
            path: 'accuracy',
            title: 'Checker accuracy',
            loadComponent: () =>
              import('./features/compliance/Reports/pages/accuracy').then((m) => m.ReportAccuracy),
          },
          // The drill-down's last two hops. Both sit under their own LITERAL
          // first segment rather than beside the report keys: `:reportKey` and
          // `:reportId` are the same shape, and order cannot separate two
          // routes of the same shape — only a distinct segment can.
          {
            path: 'list/:dataset',
            title: 'Compliance rows',
            loadComponent: () =>
              import('./features/compliance/Reports/pages/report-list').then(
                (m) => m.ReportListPage,
              ),
          },
          {
            path: 'detail/:reportId',
            title: 'Compliance check',
            loadComponent: () =>
              import('./features/compliance/Reports/pages/report-detail').then(
                (m) => m.ReportDetailPage,
              ),
          },
        ],
      },
      // The static aliases for the two parameterised children, declared before
      // the single-segment `:reportKey` alias so the intent is readable —
      // Angular would not confuse them anyway, since a terminal route must
      // match every segment.
      {
        path: 'compliance/reports/list/:dataset',
        title: 'Compliance rows',
        canActivate: [redirectToActivePartner('compliance', 'reports', 'list', ':dataset')],
        loadComponent: () =>
          import('./features/compliance/Reports/reports').then((m) => m.ComplianceReports),
      },
      {
        path: 'compliance/reports/detail/:reportId',
        title: 'Compliance check',
        canActivate: [redirectToActivePartner('compliance', 'reports', 'detail', ':reportId')],
        loadComponent: () =>
          import('./features/compliance/Reports/reports').then((m) => m.ComplianceReports),
      },
      {
        path: 'compliance/reports/:reportKey',
        title: 'Compliance Reports',
        canActivate: [redirectToActivePartner('compliance', 'reports', ':reportKey')],
        loadComponent: () =>
          import('./features/compliance/Reports/reports').then((m) => m.ComplianceReports),
      },
      // Access is the one sidebar-reachable compliance screen gated on a write
      // capability: it enumerates client firms and individuals by name and
      // email, which is `compliance:manage_access` data with no view-only
      // audience (wave E, plan §6). The nav entry carries the same gate, so a
      // `compliance:view`-only member neither sees the link nor passes the
      // guard by typing the URL.
      {
        path: 'compliance/access',
        title: 'Compliance Access',
        canActivate: [redirectToActivePartner('compliance', 'access')],
        loadComponent: () =>
          import('./features/compliance/Access/access').then((m) => m.ComplianceAccess),
      },
      {
        path: 'partner/:id/compliance/access',
        title: 'Compliance Access',
        canActivate: [setActivePartnerFromRoute, requireCapabilities('compliance:manage_access')],
        loadComponent: () =>
          import('./features/compliance/Access/access').then((m) => m.ComplianceAccess),
      },
      // The setup wizard is the only compliance route gated on *write* authority
      // rather than `compliance:view`: it exists to create the partner's bot, and
      // a view-only member reaching it could only be shown a form they cannot
      // submit. It is deliberately absent from the sidebar — the wizard is
      // entered from the zero-bot state on the dashboard and the Bot screen, and
      // a permanent "Set up" link would still be sitting there long after the bot
      // existed. The single-bot invariant is enforced inside the component, which
      // redirects to `compliance/bot` the moment the bot read says there is one.
      {
        path: 'compliance/setup',
        title: 'Set up Compliance',
        canActivate: [redirectToActivePartner('compliance', 'setup')],
        loadComponent: () =>
          import('./features/compliance/setup-wizard').then((m) => m.ComplianceSetupWizard),
      },
      {
        path: 'partner/:id/compliance/setup',
        title: 'Set up Compliance',
        canActivate: [setActivePartnerFromRoute, requireCapabilities('compliance:manage_bots')],
        loadComponent: () =>
          import('./features/compliance/setup-wizard').then((m) => m.ComplianceSetupWizard),
      },
      {
        path: 'compliance',
        title: 'Compliance',
        canActivate: [redirectToActivePartner('compliance')],
        loadComponent: () =>
          import('./features/compliance/Dashboard/dashboard').then((m) => m.ComplianceDashboard),
      },
      {
        path: 'partner/:id/compliance',
        title: 'Compliance',
        canActivate: [setActivePartnerFromRoute, requireCapabilities('compliance:view')],
        loadComponent: () =>
          import('./features/compliance/Dashboard/dashboard').then((m) => m.ComplianceDashboard),
      },
      {
        path: 'billing',
        title: 'Billing',
        canActivate: [redirectToActivePartner('billing')],
        loadComponent: () => import('./features/billing/billing').then((m) => m.Billing),
      },
      {
        path: 'partner/:id/billing',
        title: 'Billing',
        canActivate: [setActivePartnerFromRoute, requireCapabilities('partner:manage_billing')],
        loadComponent: () => import('./features/billing/billing').then((m) => m.Billing),
      },
      // The capability-denial landing (`capabilityDeniedUrl`). A child of the
      // shell on purpose — the opposite call from `/forbidden` above, and safe
      // for the reason that one is not: anyone who reaches a capability guard
      // has already passed `requirePartnerAccess`, so redirecting them inside
      // cannot loop. That placement is the whole point. `MembershipGate` owns
      // this outlet, so for the person whose capabilities are empty *because
      // they have no role* it replaces this page with the No role screen —
      // switcher included, which a route outside the shell cannot offer.
      //
      // It must stay ungated: a `requireCapabilities(...)` here would send its
      // own denials back to itself.
      {
        path: 'no-access',
        title: 'No access',
        loadComponent: () => import('./features/no-access/no-access').then((m) => m.NoAccess),
      },
      {
        path: '**',
        title: 'Page not found',
        loadComponent: () => import('./features/not-found/not-found').then((m) => m.NotFound),
      },
    ],
  },
];
